import React, { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import { HotelContext } from "../../context/HotelContext";
import { getDefaultHotelPath, parseHotelFeatureNames } from "../../utils/hotelNavigation";
import { getLocale, translate } from "../../utils/Translation";
import { formatShortInstallationDate } from "../../utils/installationDate";
import { getDemoHotel73MyHotelsCard, isDemoHotel73 } from "../../utils/demoHotel73Stats";
import LoadingIndicator from "../../components/v2/cards/LoadingIndicator";
import HotelIcon from "../../img/v2/hotel.svg";
import "./MyHotelsV2.css";

const MyHotelsV2 = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { sethId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [selectingId, setSelectingId] = useState(null);

  const formatSavingsValue = useCallback(
    (value, suffix) => `${Math.round(Number(value) || 0).toLocaleString(locale)}${suffix}`,
    [locale]
  );

  const formatInstallationDate = useCallback(
    (value) => formatShortInstallationDate(value, locale),
    [locale]
  );

  useEffect(() => {
    if (!auth?.isAuthenticated || !auth.user?.access_token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setHasError(false);

    fetch("/api/groups/hotels/summary", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + auth.user.access_token,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success === "OK" && Array.isArray(data.hotels)) {
          setHotels(
            data.hotels.map((hotel) => (isDemoHotel73(hotel.hId) ? { ...hotel, ...getDemoHotel73MyHotelsCard() } : hotel))
          );
          setHasError(false);
        } else {
          setHotels([]);
          setHasError(true);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("MyHotelsV2 - Error fetching hotels:", error);
        setHotels([]);
        setHasError(true);
        setLoading(false);
      });
  }, [auth?.isAuthenticated, auth.user?.access_token]);

  const handleSelectHotel = useCallback(
    (hotelId) => {
      if (!auth?.user?.access_token || selectingId) return;

      setSelectingId(hotelId);

      fetch(`/api/users/lastHotel/${hotelId}`, {
        method: "PUT",
        headers: {
          Authorization: "Bearer " + auth.user.access_token,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success !== "OK") {
            setHasError(true);
            setSelectingId(null);
            return;
          }

          sethId(hotelId);
          localStorage.setItem("hId", String(hotelId));

          return fetch(`/api/hotels/${hotelId}/subscriptions/`, {
            method: "GET",
            headers: {
              Authorization: "Bearer " + auth.user.access_token,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          })
            .then((response) => response.json())
            .then((subData) => {
              const hotelFeatures = parseHotelFeatureNames(subData);
              navigate(getDefaultHotelPath(hotelFeatures));
            });
        })
        .catch((error) => {
          console.error("MyHotelsV2 - Error selecting hotel:", error);
          setHasError(true);
        })
        .finally(() => {
          setSelectingId(null);
        });
    },
    [auth.user?.access_token, navigate, selectingId, sethId]
  );

  return (
    <div className="my-hotels-v2">
      {hasError && (
        <div className="my-hotels-v2__error">{translate("v2_my_hotels_error", lang)}</div>
      )}

      {loading ? (
        <div className="my-hotels-v2__loading">
          <LoadingIndicator />
        </div>
      ) : hotels.length === 0 ? (
        <div className="my-hotels-v2__empty">{translate("v2_my_hotels_empty", lang)}</div>
      ) : (
        <div className="my-hotels-v2__list">
          {hotels.map((hotel) => {
            const savings = hotel.savings || {};
            const isSelecting = selectingId === hotel.hId;
            const installationDate = formatInstallationDate(hotel.installationDate || hotel.hInstallationDate);

            return (
              <button
                key={hotel.hId}
                type="button"
                className="my-hotels-v2__card"
                onClick={() => handleSelectHotel(hotel.hId)}
                disabled={isSelecting}
              >
                <div className="my-hotels-v2__card-left">
                  <img src={HotelIcon} alt="" aria-hidden="true" className="my-hotels-v2__hotel-icon" />
                  <div className="my-hotels-v2__hotel-info">
                    <span className="my-hotels-v2__hotel-name">{hotel.hName}</span>
                    <span className="my-hotels-v2__hotel-city">{hotel.hCity || "—"}</span>
                    {installationDate ? (
                      <span className="my-hotels-v2__installation-date">
                        {translate("v2_my_hotels_installation_date", lang, { date: installationDate })}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="my-hotels-v2__card-right">
                  <span className="my-hotels-v2__savings-label">{translate("v2_my_hotels_savings_summary", lang)}</span>
                  <div className="my-hotels-v2__savings-values">
                    <span className="my-hotels-v2__savings-value my-hotels-v2__savings-value--eur">
                      {formatSavingsValue(savings.financialEur, "€")}
                    </span>
                    <span className="my-hotels-v2__savings-value my-hotels-v2__savings-value--water">
                      {formatSavingsValue(savings.waterLiters, "L")}
                    </span>
                    <span className="my-hotels-v2__savings-value my-hotels-v2__savings-value--energy">
                      {formatSavingsValue(savings.energyKwh, "kWh")}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyHotelsV2;
